/**
 * Minimal markdown for owner-provided help text: headings, bullet/numbered lists
 * and paragraphs. Output is a plain data structure; the UI renders it as React
 * text nodes, so owner content can never inject HTML, links or images.
 */
export type SafeMarkdownBlock =
  | Readonly<{ kind: "heading"; text: string }>
  | Readonly<{ kind: "paragraph"; text: string }>
  | Readonly<{ kind: "ul" | "ol"; items: readonly string[] }>;

const HEADING = /^#{1,4}\s+(.*\S)\s*$/u;
const BULLET = /^\s*[-*]\s+(.*\S)\s*$/u;
const ORDERED = /^\s*\d+[.)]\s+(.*\S)\s*$/u;

export const parseSafeMarkdown = (source: string): readonly SafeMarkdownBlock[] => {
  const blocks: SafeMarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { kind: "ul" | "ol"; items: string[] } | undefined;
  const flushParagraph = (): void => {
    if (paragraph.length) blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = (): void => {
    if (list) blocks.push(list);
    list = undefined;
  };
  for (const line of source.replace(/\r\n?/gu, "\n").split("\n")) {
    if (line.trim() === "") { flushParagraph(); flushList(); continue; }
    const heading = HEADING.exec(line);
    if (heading) { flushParagraph(); flushList(); blocks.push({ kind: "heading", text: heading[1]! }); continue; }
    const bullet = BULLET.exec(line);
    const ordered = ORDERED.exec(line);
    const item = bullet ?? ordered;
    if (item) {
      flushParagraph();
      const kind = bullet ? "ul" : "ol";
      if (list && list.kind !== kind) flushList();
      list ??= { kind, items: [] };
      list.items.push(item[1]!);
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
};

/** Splits "**bold**" markers into text/bold segments. */
export const parseInlineBold = (text: string): readonly Readonly<{ bold: boolean; text: string }>[] =>
  text.split(/(\*\*[^*]+\*\*)/gu).filter(Boolean).map((part) =>
    /^\*\*[^*]+\*\*$/u.test(part) ? { bold: true, text: part.slice(2, -2) } : { bold: false, text: part });
